import { useState, useEffect } from 'react';
import { Save, Key, Shield, ShieldCheck, ShieldOff, RefreshCw } from 'lucide-react';
import { setupMfa, enableMfa, disableMfa } from '../services/portalApi';

function MfaSetupModal({ onClose, onEnabled }) {
  const [phase, setPhase]   = useState('loading'); // loading | scan | verify | done
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code,   setCode]   = useState('');
  const [error,  setError]  = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setupMfa()
      .then(d => { setQrCode(d.qrCode); setSecret(d.secret); setPhase('scan'); })
      .catch(e => { setError(e.message); setPhase('scan'); });
  }, []);

  const handleEnable = async () => {
    setSaving(true);
    setError('');
    try {
      await enableMfa(code.replace(/\s/g, ''));
      setPhase('done');
      setTimeout(() => { onEnabled(); onClose(); }, 1200);
    } catch (e) {
      setError(e.message);
      setCode('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Shield size={16} color="var(--info)"/> Set up two-factor authentication
          </div>
          <button className="btn btn-secondary btn-xs" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {phase === 'loading' && (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--muted-3)' }}>
              <RefreshCw size={18} style={{ animation: 'spin 0.8s linear infinite' }}/>
            </div>
          )}

          {(phase === 'scan' || phase === 'verify') && (
            <>
              <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
                Scan this QR code with your authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.), then enter the 6-digit code to confirm.
              </p>

              {qrCode && (
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <img src={qrCode} alt="MFA QR code" style={{ width: 180, height: 180, borderRadius: 8, background: 'var(--on-brand)', padding: 8 }}/>
                </div>
              )}

              {secret && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: 'var(--muted-3)', marginBottom: 4 }}>Or enter this key manually:</div>
                  <div style={{
                    fontFamily: 'monospace', fontSize: 13, letterSpacing: 2,
                    background: 'var(--bg)', border: '1px solid var(--border-strong)',
                    borderRadius: 6, padding: '8px 12px', color: 'var(--purple)',
                    wordBreak: 'break-all', textAlign: 'center',
                  }}>{secret}</div>
                </div>
              )}

              <div className="form-group" style={{ marginBottom: 4 }}>
                <label className="form-label">Verification code</label>
                <input
                  className="form-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={e => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && code.length === 6 && handleEnable()}
                  placeholder="000000"
                  style={{ textAlign: 'center', fontSize: 20, letterSpacing: 6, fontFamily: 'monospace' }}
                  autoFocus
                />
              </div>
              {error && <div style={{ fontSize: 12, color: 'var(--err)', marginTop: 4 }}>{error}</div>}
            </>
          )}

          {phase === 'done' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <ShieldCheck size={40} color="var(--ok)" style={{ marginBottom: 12 }}/>
              <div style={{ color: 'var(--ok)', fontWeight: 600 }}>MFA enabled successfully!</div>
            </div>
          )}
        </div>
        {(phase === 'scan' || phase === 'verify') && (
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={handleEnable}
              disabled={saving || code.length < 6}
            >
              {saving ? <RefreshCw size={12} style={{ animation: 'spin 0.8s linear infinite' }}/> : <ShieldCheck size={12}/>}
              {saving ? 'Verifying…' : 'Enable MFA'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function MfaDisableModal({ onClose, onDisabled }) {
  const [code,   setCode]   = useState('');
  const [error,  setError]  = useState('');
  const [saving, setSaving] = useState(false);

  const handleDisable = async () => {
    setSaving(true);
    setError('');
    try {
      await disableMfa(code.replace(/\s/g, ''));
      onDisabled();
      onClose();
    } catch (e) {
      setError(e.message);
      setCode('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title" style={{ color: 'var(--err)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShieldOff size={16}/> Disable two-factor authentication
          </div>
          <button className="btn btn-secondary btn-xs" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
            Enter the current 6-digit code from your authenticator app to confirm you want to disable MFA.
          </p>
          <div className="form-group" style={{ marginBottom: 4 }}>
            <input
              className="form-input"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={e => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && code.length === 6 && handleDisable()}
              placeholder="000000"
              style={{ textAlign: 'center', fontSize: 20, letterSpacing: 6, fontFamily: 'monospace' }}
              autoFocus
            />
          </div>
          {error && <div style={{ fontSize: 12, color: 'var(--err)', marginTop: 4 }}>{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-sm"
            style={{ background: 'var(--err-bg)', color: 'var(--err)', border: '1px solid var(--err-btn-bg)' }}
            onClick={handleDisable}
            disabled={saving || code.length < 6}
          >
            {saving ? <RefreshCw size={12} style={{ animation: 'spin 0.8s linear infinite' }}/> : <ShieldOff size={12}/>}
            {saving ? 'Disabling…' : 'Disable MFA'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Settings({ user }) {
  const [tab,        setTab]        = useState('account');
  const [saved,      setSaved]      = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(Boolean(user?.mfaEnabled));
  const [showSetup,  setShowSetup]  = useState(false);
  const [showDisable,setShowDisable]= useState(false);

  const handleSave = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };

  return (
    <div className="content">
      <div className="card">
        <div style={{ padding: '0 20px' }}>
          <div className="tabs">
            {[['account','Account'],['api','API & Webhooks'],['security','Security'],['billing','Billing'],['notifications','Notifications']].map(([id,label]) => (
              <div key={id} className={`tab ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</div>
            ))}
          </div>
        </div>

        <div className="card-body" style={{ maxWidth: 560 }}>
          {tab === 'account' && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Company Name</label>
                  <input className="form-input" defaultValue="Damovo Customer"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Account ID</label>
                  <input className="form-input" defaultValue="DMV-CUST-001" disabled style={{ opacity: .5 }}/>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Primary Contact Email</label>
                <input className="form-input" type="email" defaultValue="admin@damovo-customer.com"/>
              </div>
              <div className="form-group">
                <label className="form-label">Technical Contact Email</label>
                <input className="form-input" type="email" defaultValue="noc@damovo-customer.com"/>
              </div>
              <div className="form-group">
                <label className="form-label">Time Zone</label>
                <select className="form-input select-input">
                  <option>America/Los_Angeles (UTC-7)</option>
                  <option>America/New_York (UTC-4)</option>
                  <option>UTC</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Default Emergency Location (E911)</label>
                <input className="form-input" defaultValue="123 Main St, San Francisco CA 94105"/>
              </div>
            </>
          )}

          {tab === 'api' && (
            <>
              <div className="alert alert-info mb-20">
                <Key size={14}/>
                <span>API keys are used to authenticate Voxbone number management requests and AudioCodes REST API calls.</span>
              </div>
              {[
                { label: 'Voxbone API Token', value: 'bw_live_••••••••••••••••••••4a2f', provider: 'Voxbone' },
                { label: 'Voxbone Account ID', value: 'BW-DMV-CUST-001', provider: 'Voxbone' },
                { label: 'AudioCodes REST API Key', value: 'ac_••••••••••••••••••••8b1c', provider: 'AudioCodes' },
                { label: 'Portal API Key (outbound)', value: 'sk_live_••••••••••••••••••••3d7e', provider: 'Damovo' },
              ].map(item => (
                <div key={item.label} className="form-group">
                  <label className="form-label">{item.label} <span className="badge badge-gray" style={{ marginLeft: 6 }}>{item.provider}</span></label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input className="form-input font-mono" defaultValue={item.value} style={{ flex: 1 }}/>
                    <button className="btn btn-secondary btn-sm">Reveal</button>
                    <button className="btn btn-secondary btn-sm">Rotate</button>
                  </div>
                </div>
              ))}
              <div className="form-group">
                <label className="form-label">Webhook URL (number change events)</label>
                <input className="form-input font-mono" placeholder="https://your-system.example.com/webhooks/draas"/>
              </div>
            </>
          )}

          {tab === 'security' && (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px',
                background: 'var(--bg)', border: `1px solid ${mfaEnabled ? '#14532d44' : 'var(--border)'}`,
                borderRadius: 10, marginBottom: 20,
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                  background: mfaEnabled ? 'var(--ok-bg)' : 'var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {mfaEnabled
                    ? <ShieldCheck size={22} color="var(--ok)"/>
                    : <Shield size={22} color="var(--muted-2)"/>}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: 'var(--text-2)', marginBottom: 2 }}>
                    Two-Factor Authentication (TOTP)
                  </div>
                  <div style={{ fontSize: 12, color: mfaEnabled ? 'var(--ok)' : 'var(--muted-2)' }}>
                    {mfaEnabled
                      ? 'Active — your account is protected with an authenticator app'
                      : 'Not enabled — add an extra layer of security to your account'}
                  </div>
                </div>
                <span className={`badge ${mfaEnabled ? 'badge-green' : 'badge-gray'}`}>
                  {mfaEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>

              {mfaEnabled ? (
                <button
                  className="btn btn-sm"
                  style={{ background: 'var(--err-bg)', color: 'var(--err)', border: '1px solid var(--err-bg)' }}
                  onClick={() => setShowDisable(true)}
                >
                  <ShieldOff size={13}/> Disable MFA
                </button>
              ) : (
                <button className="btn btn-primary btn-sm" onClick={() => setShowSetup(true)}>
                  <ShieldCheck size={13}/> Enable MFA
                </button>
              )}

              <div className="divider" style={{ margin: '24px 0' }}/>
              <div style={{ fontSize: 12, color: 'var(--muted-3)', lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--muted)' }}>Supported apps:</strong> Google Authenticator, Microsoft Authenticator, Authy, 1Password, and any TOTP-compatible app.
                After enabling, you'll be asked for a 6-digit code each time you sign in.
              </div>
            </>
          )}

          {tab === 'billing' && (
            <>
              <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: 'var(--muted-3)', marginBottom: 4 }}>Current Plan</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>Damovo Hosting Enterprise</div>
                <div style={{ fontSize: 12, color: 'var(--muted-2)', marginTop: 2 }}>15 DIDs · 3 SBCs · 6 platforms · Unlimited routing rules</div>
                <div style={{ marginTop: 8 }}>
                  <span className="badge badge-green">Active</span>
                  <span style={{ fontSize: 12, color: 'var(--muted-3)', marginLeft: 8 }}>Next invoice: 2026-06-01</span>
                </div>
              </div>
              {[
                ['DID Numbers (15)', '$75.00/mo'],
                ['SBC Instances (3)', '$450.00/mo'],
                ['Platform Licences', '$150.00/mo'],
                ['Voxbone SIP Trunks', '$220.00/mo'],
              ].map(([item, price]) => (
                <div key={item} className="trunk-row">
                  <span style={{ fontSize: 13, color: 'var(--muted)' }}>{item}</span>
                  <span style={{ fontSize: 13, fontFamily: 'monospace', color: 'var(--text-2)' }}>{price}</span>
                </div>
              ))}
              <div className="trunk-row">
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Total</span>
                <span style={{ fontSize: 14, fontWeight: 700, fontFamily: 'monospace', color: 'var(--ok)' }}>$895.00/mo</span>
              </div>
            </>
          )}

          {tab === 'notifications' && (
            <>
              {[
                'SBC health alerts (CPU/memory threshold exceeded)',
                'SIP trunk UP/DOWN events',
                'Voxbone port-in order status changes',
                'Number suspended or restored',
                'Failed call rate exceeds 5%',
                'Firmware update available for SBC',
                'Billing invoice generated',
              ].map(item => (
                <div key={item} className="trunk-row">
                  <span style={{ fontSize: 13, color: 'var(--muted)' }}>{item}</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" defaultChecked style={{ accentColor: 'var(--purple)' }}/>
                    <span style={{ fontSize: 12, color: 'var(--muted-3)' }}>Email</span>
                    <input type="checkbox" defaultChecked style={{ accentColor: 'var(--purple)' }}/>
                    <span style={{ fontSize: 12, color: 'var(--muted-3)' }}>Webhook</span>
                  </label>
                </div>
              ))}
            </>
          )}

          {tab !== 'security' && (
            <div style={{ marginTop: 20 }}>
              <button className="btn btn-primary" onClick={handleSave}>
                <Save size={13}/> {saved ? 'Saved!' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>

      {showSetup   && <MfaSetupModal   onClose={() => setShowSetup(false)}   onEnabled={() => setMfaEnabled(true)} />}
      {showDisable && <MfaDisableModal onClose={() => setShowDisable(false)} onDisabled={() => setMfaEnabled(false)} />}
    </div>
  );
}
